"use client";
import React, { useEffect, useRef } from "react";
import styles from "./DmsIntegrations.module.css";
import {
  dmsIntegrationCategories as CATEGORIES,
  dmsIntegrationLogos as RAIL_LOGOS,
} from "../../data/document-management-system";

export default function DmsIntegrationsSection() {
  const gridRef = useRef(null);
  const railRef = useRef(null);

  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;

    const gridObs = new IntersectionObserver(
      (entries) =>
        entries.forEach(
          (e) => e.isIntersecting && e.target.classList.add(styles.inView)
        ),
      { threshold: 0.15 }
    );
    gridObs.observe(grid);

    const rail = railRef.current;
    let railObs;
    if (rail) {
      railObs = new IntersectionObserver(
        (entries) =>
          entries.forEach(
            (e) => e.isIntersecting && e.target.classList.add(styles.inView)
          ),
        { threshold: 0.1 }
      );
      railObs.observe(rail);
    }

    const cards = grid.querySelectorAll(`.${styles.card}`);
    const cardObs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.inView);
            cardObs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2, rootMargin: "0px 0px -12% 0px" }
    );
    cards.forEach((c) => cardObs.observe(c));

    return () => {
      gridObs.disconnect();
      railObs && railObs.disconnect();
      cardObs.disconnect();
    };
  }, []);

  return (
    <section className={`${styles.section} py-5`}>
      <div className="container">
        <header className={styles.head} data-reveal="fade">
          <div className={styles.eyebrow}>
            Integration Ecosystem — Connected Beyond Boundaries
          </div>
          <h3 className={styles.title}>
            Your DMS Doesn’t Work Alone — It Works With Everything You Use.
          </h3>
          <p className={styles.subtitle}>
            CodeQlik’s DMS seamlessly integrates with your existing tools,
            keeping your ecosystem unified. Whether you’re approving contracts
            from Slack, syncing invoices from ERPNext, or retrieving files from
            Google Drive — everything flows effortlessly.
          </p>
        </header>

        {/* Logo rail showcasing popular integrations */}
        <div ref={railRef} className={styles.logoRail} aria-hidden>
          <div className={styles.logoTrack}>
            <div className={styles.railInner}>
              {RAIL_LOGOS.map((l, i) => (
                <span key={`${l.name}-${i}`} className={styles.logoChip}>
                  <i className={l.icon}></i>
                  {l.name}
                </span>
              ))}
            </div>
            {/* Duplicate for seamless looping */}
            <div className={styles.railInner}>
              {RAIL_LOGOS.map((l, i) => (
                <span key={`dup-${l.name}-${i}`} className={styles.logoChip}>
                  <i className={l.icon}></i>
                  {l.name}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div ref={gridRef} className={styles.grid}>
          {CATEGORIES.map((cat, idx) => (
            <div key={cat.title} className={styles.cardWrap}>
              <article
                className={`${styles.card} ${
                  styles[`cardDelay${Math.min(idx + 1, 5)}`]
                }`}
              >
                <div className={styles.cardHead}>
                  <span className={styles.icon} aria-hidden>
                    <i className={cat.icon}></i>
                  </span>
                  <h4 className={styles.catTitle}>{cat.title}</h4>
                </div>
                <div className={styles.examples}>
                  {cat.examples.map((ex) => (
                    <span key={ex} className={styles.chip}>
                      <i
                        className="fa-solid fa-circle"
                        style={{ fontSize: 6 }}
                      ></i>
                      {ex}
                    </span>
                  ))}
                </div>
                <div className={styles.benefit}>
                  <i className="fa-solid fa-bolt"></i>
                  {cat.benefit}
                </div>
              </article>
            </div>
          ))}
        </div>

        <p className={styles.cta}>
          <em>
            We build bridges between your tools — so your workflows never hit a
            wall.
          </em>
        </p>
      </div>
    </section>
  );
}
